import { useCallback, useEffect, useMemo } from 'react';
import to from 'await-to-js';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useAPIClientRequest, useAuthenticatedUser } from 'c-hooks';
import { useEntityData } from 'c-data';
import ApiClient from 'c-data/apiClient';
import type { Campaign, CampaignReportingAssignedVia } from 'c-sdk';
import {
    atom_campaignSummaryLayoutConfigs,
    atom_campaignSummaryWidgetConfigs,
    atom_campaignTopBoxConfigs,
    atom_useLinearReportingOverride,
} from 'c-main/Pages/Campaign/CampaignOverviewPage/atoms';
import type {
    SummaryLayoutConfig,
    SummaryWidgetConfig,
    TopBoxConfig,
} from './summaryCustomizationConfig';
import {
    getSummaryWidgetConfigKey,
    normalizeSummaryLayout,
    normalizeSummaryWidgetConfig,
} from './summaryCustomizationConfig';

// Nearest assignment first, matching how the API resolves. Only used to pick a sensible
// default; once someone chooses a summary explicitly, their choice wins.
const PRECEDENCE: CampaignReportingAssignedVia[] = ['user', 'buyer', 'agency', 'client', 'owner'];

export const DEFAULT_SUMMARY_SOURCE_KEY = 'default';
export const DEFAULT_SUMMARY_SOURCE_NAME = 'Standard summary';

type SummaryCampaignType = 'linear' | 'dsp';

export type SummarySource = {
    key: string;
    name: string;
    kind: 'default' | 'assigned';
    /** Set for assigned sources only. */
    id?: number;
    assignedVia?: CampaignReportingAssignedVia;
};

export type LoadedSummaryConfig = {
    widgets: SummaryWidgetConfig[];
    topBoxes: Record<string, TopBoxConfig>;
    layout: SummaryLayoutConfig;
};

// Which summary each campaign type shows: 'default' for the built-in one, or `assigned:<id>`.
// Linear and DSP are remembered separately because a summary is saved against one of them.
// Kept per browser so the choice survives a reload; an unknown or withdrawn key falls back.
const SELECTED_KEY = 'summary.selected.v1';
type SelectedKeys = Partial<Record<SummaryCampaignType, string>>;

const loadSelectedKeys = (): SelectedKeys => {
    try {
        return JSON.parse(window.localStorage.getItem(SELECTED_KEY) ?? '{}') ?? {};
    } catch {
        return {};
    }
};

const persistSelectedKeys = (keys: SelectedKeys): void => {
    try {
        window.localStorage.setItem(SELECTED_KEY, JSON.stringify(keys));
    } catch {
        // ignore storage failures (private mode, quota, etc.)
    }
};

const atom_selectedSummaryKeys = atom<SelectedKeys>(loadSelectedKeys());

// Fetched configs, kept for the session: the Summary remounts on every environment/source change
// and on switching campaign, and a config already read shouldn't be fetched again. 'error' marks a config
// that couldn't be fetched or read, so it isn't retried on every render.
const atom_summaryConfigCache = atom<Record<number, LoadedSummaryConfig | 'error'>>({});
const inFlight = new Set<number>();

// Which source was last written into the per-campaign summary atoms, keyed like those atoms.
// Lets a viewer's own Customize Summary edits stand until they pick a different summary.
const atom_appliedSummarySource = atom<Record<string, string>>({});

/** A stored summary config (JSON) as the Summary and its editor use it. Throws on bad JSON. */
export const parseSummaryConfig = (raw: string): LoadedSummaryConfig => {
    const parsed = JSON.parse(raw) ?? {};
    const widgets = Array.isArray(parsed.widgets)
        ? parsed.widgets.map(normalizeSummaryWidgetConfig)
        : [];
    return {
        widgets,
        topBoxes: parsed.topBoxes ?? {},
        layout: normalizeSummaryLayout(parsed.layout, widgets),
    };
};

/** Whether a campaign's Summary renders its DSP variant, matching CampaignAnalyticsES. */
export const useSummaryIsDsp = (campaignId: number) => {
    const { getById } = useEntityData<Campaign>('Campaign');
    const campaign = getById({ id: campaignId });
    const useLinearReportingOverride = useAtomValue(atom_useLinearReportingOverride);
    const hasDspReporting =
        campaign != null && campaign.type !== 'linear' && campaign.type !== 'default';
    return hasDspReporting && !useLinearReportingOverride;
};

/**
 * Every summary this user can view on a campaign's Summary tab, and which one is showing.
 *
 * The built-in summary is always available. On top of it, any number of saved summaries can be
 * assigned by an admin — directly or through an agency, buyer, brand or media owner. `/user/me`
 * carries the assignments without their payload, so a config's contents are fetched once, when
 * it is first selected, and cached for the session. `enabled` false (Summary 2.0 off) still
 * resolves the list but fetches nothing, since only Summary 2.0 can show an assigned summary.
 */
export const useSummarySources = (isDsp: boolean, enabled = true) => {
    const { user } = useAuthenticatedUser();
    const campaignType: SummaryCampaignType = isDsp ? 'dsp' : 'linear';
    const [selectedKeys, setSelectedKeys] = useAtom(atom_selectedSummaryKeys);
    const [cache, setCache] = useAtom(atom_summaryConfigCache);
    const { start: readConfig } = useAPIClientRequest(ApiClient.Entities.CampaignReporting.get);

    // A summary saved without a campaign type is offered on both; otherwise only on its own.
    const assignments = useMemo(
        () =>
            (user?.campaign_reporting_configs ?? []).filter(
                a =>
                    a.type === 'summary' &&
                    (a.campaign_type == null || a.campaign_type === campaignType),
            ),
        [user, campaignType],
    );

    const sources = useMemo<SummarySource[]>(
        () => [
            {
                key: DEFAULT_SUMMARY_SOURCE_KEY,
                name: DEFAULT_SUMMARY_SOURCE_NAME,
                kind: 'default' as const,
            },
            ...assignments.map(a => ({
                key: `assigned:${a.id}`,
                name: a.name,
                kind: 'assigned' as const,
                id: a.id,
                assignedVia: a.assigned_via,
            })),
        ],
        [assignments],
    );

    const selectedKey = selectedKeys[campaignType];
    const activeKey = useMemo(() => {
        if (selectedKey != null && sources.some(s => s.key === selectedKey)) return selectedKey;
        const nearest = PRECEDENCE.map(via => assignments.find(a => a.assigned_via === via)).find(
            a => a != null,
        );
        return nearest ? `assigned:${nearest.id}` : DEFAULT_SUMMARY_SOURCE_KEY;
    }, [selectedKey, sources, assignments]);

    const active = useMemo(
        () => sources.find(s => s.key === activeKey) ?? sources[0],
        [sources, activeKey],
    );

    const activeId = active.kind === 'assigned' ? active.id : null;
    const cached = activeId != null ? cache[activeId] : undefined;

    useEffect(() => {
        if (!enabled || activeId == null || cached != null || inFlight.has(activeId)) return;
        inFlight.add(activeId);
        (async () => {
            const [err, res] = await to(readConfig(activeId));
            inFlight.delete(activeId);
            let entry: LoadedSummaryConfig | 'error' = 'error';
            const raw = (res as any)?.data?.data?.config;
            if (!err && raw) {
                try {
                    entry = parseSummaryConfig(raw);
                } catch {
                    // A config we can't read is marked and not retried this session.
                }
            }
            setCache(current => ({ ...current, [activeId]: entry }));
        })();
    }, [enabled, activeId, cached, readConfig, setCache]);

    const activeConfig = cached != null && cached !== 'error' ? cached : null;
    const isLoading = activeId != null && cached == null;
    const hasError = cached === 'error';

    const select = useCallback(
        (key: string) => {
            setSelectedKeys(current => {
                const next = { ...current, [campaignType]: key };
                persistSelectedKeys(next);
                return next;
            });
        },
        [setSelectedKeys, campaignType],
    );

    return { sources, active, activeKey, activeConfig, isLoading, hasError, select };
};

/**
 * Writes the active summary into the per-campaign atoms CampaignAnalyticsES reads, the same way
 * loading a saved summary in the Customize Summary dialog does. Only acts when the source
 * changes, so edits made in that dialog afterwards aren't overwritten on the next render.
 * Returns whether the atoms now hold the active summary.
 */
export const useApplySummarySource = (
    campaignId: number,
    isDsp: boolean,
    active: SummarySource,
    activeConfig: LoadedSummaryConfig | null,
) => {
    const [applied, setApplied] = useAtom(atom_appliedSummarySource);
    const setWidgets = useSetAtom(atom_campaignSummaryWidgetConfigs);
    const setTopBoxes = useSetAtom(atom_campaignTopBoxConfigs);
    const setLayouts = useSetAtom(atom_campaignSummaryLayoutConfigs);
    const configKey = getSummaryWidgetConfigKey(campaignId, isDsp);
    const appliedKey = applied[configKey];

    useEffect(() => {
        if (appliedKey === active.key) return;

        if (active.kind === 'assigned') {
            if (activeConfig == null) return;
            setWidgets(current => ({ ...current, [configKey]: activeConfig.widgets }));
            setTopBoxes(current => ({ ...current, [configKey]: activeConfig.topBoxes }));
            setLayouts(current => ({ ...current, [configKey]: activeConfig.layout }));
        } else if (appliedKey != null) {
            // Switching back from an assigned summary to the built-in one. If nothing was ever
            // applied, leave the atoms alone: they may hold the viewer's own customisation.
            const omitKey = <T>(current: Record<string, T>) => {
                const { [configKey]: _removed, ...rest } = current;
                return rest;
            };
            setWidgets(omitKey);
            setTopBoxes(omitKey);
            setLayouts(omitKey);
        }
        setApplied(current => ({ ...current, [configKey]: active.key }));
    }, [
        active,
        activeConfig,
        appliedKey,
        configKey,
        setApplied,
        setLayouts,
        setTopBoxes,
        setWidgets,
    ]);

    // Until this is true the atoms still hold the previous summary, and rendering would flash it.
    // Nothing applied yet on the built-in summary means the atoms are already as they should be.
    return appliedKey === active.key || (active.kind === 'default' && appliedKey == null);
};
